import { useState } from "react";
import ReactECharts from "echarts-for-react";
import type { ClusterItem } from "@/types/jobMarket";

interface Props {
  clusters: ClusterItem[];
}

/** Пастельные темы под bento-карточки (фон, бордер, акцент, чипы) */
const CLUSTER_THEMES: Record<
  string,
  { bg: string; border: string; accent: string; tag: string; tagText: string }
> = {
  modern_lakehouse: {
    bg: "bg-teal-50/90",
    border: "border-teal-200/80",
    accent: "text-teal-700",
    tag: "bg-teal-100/90",
    tagText: "text-teal-800",
  },
  clickhouse_stack: {
    bg: "bg-emerald-50/90",
    border: "border-emerald-200/80",
    accent: "text-emerald-700",
    tag: "bg-emerald-100/90",
    tagText: "text-emerald-800",
  },
  spark_ecosystem: {
    bg: "bg-orange-50/90",
    border: "border-orange-200/80",
    accent: "text-orange-700",
    tag: "bg-orange-100/90",
    tagText: "text-orange-800",
  },
  databricks_platform: {
    bg: "bg-violet-50/90",
    border: "border-violet-200/80",
    accent: "text-violet-700",
    tag: "bg-violet-100/90",
    tagText: "text-violet-800",
  },
  dbt_orchestration: {
    bg: "bg-amber-50/90",
    border: "border-amber-200/80",
    accent: "text-amber-700",
    tag: "bg-amber-100/90",
    tagText: "text-amber-800",
  },
  cloud_aws: {
    bg: "bg-rose-50/90",
    border: "border-rose-200/80",
    accent: "text-rose-700",
    tag: "bg-rose-100/90",
    tagText: "text-rose-800",
  },
  cloud_gcp: {
    bg: "bg-sky-50/90",
    border: "border-sky-200/80",
    accent: "text-sky-700",
    tag: "bg-sky-100/90",
    tagText: "text-sky-800",
  },
};

const FALLBACK_THEME = {
  bg: "bg-slate-50/90",
  border: "border-slate-200/80",
  accent: "text-slate-700",
  tag: "bg-slate-100/90",
  tagText: "text-slate-800",
};

const CLUSTER_COLORS: Record<string, string> = {
  modern_lakehouse: "#14b8a6",
  clickhouse_stack: "#0abf53",
  spark_ecosystem: "#f59e0b",
  databricks_platform: "#8b5cf6",
  dbt_orchestration: "#f97316",
  cloud_aws: "#ef4444",
  cloud_gcp: "#3b82f6",
};

function themeFor(id: string) {
  return CLUSTER_THEMES[id] ?? FALLBACK_THEME;
}

const MAX_CHART_COMPANIES = 15;

/** Агрегат «Прочие (N компаний)» с бэкенда — в графике не показываем */
function isOthersBucket(employer: string): boolean {
  return employer.trim().startsWith("Прочие");
}

function ClusterBentoCard({
  cluster,
  emphasis,
  onSelect,
  minHeightPx,
}: {
  cluster: ClusterItem;
  /** 0..1 относительно лидера по вакансиям — типографика и мин. высота */
  emphasis: number;
  onSelect: () => void;
  minHeightPx?: number;
}) {
  const t = themeFor(cluster.id);
  const e = Math.min(1, Math.max(0, emphasis));
  const techShow = Math.max(3, Math.min(10, Math.round(3 + e * 7)));
  const titleClass =
    e >= 0.55 ? "text-lg md:text-xl" : e >= 0.28 ? "text-base md:text-lg" : "text-sm";
  const descClamp = e >= 0.45 ? "line-clamp-3" : "line-clamp-2";
  const pad = e >= 0.45 ? "p-5" : "p-4";
  const h = minHeightPx ?? Math.round(96 + e * 132);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        "text-left rounded-xl border transition-all duration-200 w-full h-full",
        "hover:shadow-md hover:brightness-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
        "flex flex-col",
        t.bg,
        t.border,
        pad,
      ].join(" ")}
      style={{ minHeight: `${h}px` }}
    >
      <div className={`text-sm font-bold tabular-nums ${t.accent}`}>
        {cluster.vacancy_count.toLocaleString("ru-RU")} вак.
      </div>
      <div className={`font-semibold text-neutral-text leading-tight mt-2 ${titleClass}`}>
        {cluster.name}
      </div>
      <p className={`text-xs text-neutral-text/55 leading-snug mt-1.5 ${descClamp}`}>
        {cluster.description}
      </p>
      <div className="flex flex-wrap gap-1.5 mt-auto pt-3">
        {cluster.cluster_techs.slice(0, techShow).map((tech) => (
          <span
            key={tech}
            className={`text-[10px] md:text-[11px] px-2 py-0.5 rounded-md font-medium ${t.tag} ${t.tagText}`}
          >
            {tech}
          </span>
        ))}
        {cluster.cluster_techs.length > techShow && (
          <span className="text-[10px] text-neutral-text/45 self-center">+{cluster.cluster_techs.length - techShow}</span>
        )}
      </div>
    </button>
  );
}

export function ClusterView({ clusters }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const active = [...clusters]
    .filter((c) => c.vacancy_count > 0)
    .sort((a, b) => b.vacancy_count - a.vacancy_count);

  if (!active.length) {
    return (
      <div className="flex items-center justify-center h-20 text-gray-400 text-sm">
        Нет данных
      </div>
    );
  }

  const selected = active.find((c) => c.id === selectedId) ?? null;
  const first = active[0];
  const second = active[1];
  const rest = active.slice(2);
  const maxVac = first.vacancy_count || 1;

  return (
    <div key={selectedId ?? "__grid__"} style={{ animation: "fadeIn 0.25s ease" }}>
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      {selected ? (
        <div className="flex gap-4 flex-col lg:flex-row">
          <button
            type="button"
            onClick={() => setSelectedId(null)}
            className="flex-shrink-0 rounded-xl px-4 py-4 flex flex-col gap-2 text-left focus:outline-none hover:brightness-95 transition-all self-start max-w-[280px]"
            style={{
              background: `${CLUSTER_COLORS[selected.id] ?? "#6b7280"}12`,
              border: `1.5px solid ${CLUSTER_COLORS[selected.id] ?? "#6b7280"}50`,
              boxShadow: `0 0 0 2px ${CLUSTER_COLORS[selected.id] ?? "#6b7280"}20`,
            }}
          >
            <div className="font-semibold text-gray-800 text-sm leading-tight">{selected.name}</div>
            <div className="flex flex-wrap gap-1">
              {selected.cluster_techs.map((tech) => (
                <span
                  key={tech}
                  className="text-[10px] px-1.5 py-0 rounded font-medium leading-4"
                  style={{
                    background: `${CLUSTER_COLORS[selected.id] ?? "#6b7280"}18`,
                    color: CLUSTER_COLORS[selected.id] ?? "#6b7280",
                  }}
                >
                  {tech}
                </span>
              ))}
            </div>
            <div className="text-xs font-bold" style={{ color: CLUSTER_COLORS[selected.id] ?? "#6b7280" }}>
              {selected.vacancy_count.toLocaleString()} вак.
            </div>
            <div className="text-xs text-gray-400 leading-snug">{selected.description}</div>
          </button>

          <div className="flex-1 min-w-0">
            {selected.companies.some((c) => !isOthersBucket(c.employer)) ? (
              <CompanyChart
                companies={selected.companies}
                color={CLUSTER_COLORS[selected.id] ?? "#0abf53"}
              />
            ) : (
              <p className="text-xs text-gray-400 pt-2">Нет данных о компаниях</p>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Верхний ряд: ширина flex ∝ вакансиям; один кластер — на всю ширину */}
          <div
            className={
              second ? "flex flex-col md:flex-row gap-3 md:items-stretch" : "w-full"
            }
          >
            <div
              className="min-w-0 flex"
              style={{ flex: second ? first.vacancy_count : 1 }}
            >
              <ClusterBentoCard
                cluster={first}
                emphasis={first.vacancy_count / maxVac}
                onSelect={() => setSelectedId(first.id)}
                minHeightPx={
                  second
                    ? Math.round(140 + (first.vacancy_count / maxVac) * 88)
                    : Math.round(160 + (first.vacancy_count / maxVac) * 80)
                }
              />
            </div>
            {second && (
              <div className="min-w-0 md:min-w-[200px] flex" style={{ flex: second.vacancy_count }}>
                <ClusterBentoCard
                  cluster={second}
                  emphasis={second.vacancy_count / maxVac}
                  onSelect={() => setSelectedId(second.id)}
                  minHeightPx={Math.round(120 + (second.vacancy_count / maxVac) * 100)}
                />
              </div>
            )}
          </div>

          {/* Нижний ряд: flex-wrap, доля ширины ∝ вакансиям */}
          {rest.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {rest.map((cluster) => (
                <div
                  key={cluster.id}
                  className="min-w-[min(100%,148px)] max-w-full flex"
                  style={{ flex: `${cluster.vacancy_count} 1 152px` }}
                >
                  <ClusterBentoCard
                    cluster={cluster}
                    emphasis={cluster.vacancy_count / maxVac}
                    onSelect={() => setSelectedId(cluster.id)}
                    minHeightPx={Math.round(100 + (cluster.vacancy_count / maxVac) * 72)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const EMPLOYER_RENAME: Record<string, string> = {
  Тинькофф: "Т-Банк",
};

function normalizeEmployer(name: string): string {
  return EMPLOYER_RENAME[name] ?? name;
}

function CompanyChart({
  companies,
  color,
}: {
  companies: { employer: string; vacancies: number; techs: string[] }[];
  color: string;
}) {
  const filtered = companies.filter((c) => !isOthersBucket(c.employer));
  const sorted = [...filtered]
    .sort((a, b) => b.vacancies - a.vacancies)
    .slice(0, MAX_CHART_COMPANIES)
    .map((c) => ({ ...c, employer: normalizeEmployer(c.employer) }));

  const option = {
    tooltip: {
      trigger: "axis" as const,
      appendToBody: true,
      confine: false,
      axisPointer: { type: "shadow" as const },
      formatter: (params: { name: string; value: number }[]) => {
        const p = params[0];
        const co = filtered.find((c) => normalizeEmployer(c.employer) === p.name);
        let html = `<strong>${p.name}</strong>: ${p.value} вак.`;
        if (co?.techs.length) {
          html += `<br/><span style="color:#9ca3af;font-size:11px">${co.techs.join(", ")}</span>`;
        }
        return html;
      },
    },
    grid: { left: 8, right: 40, top: 4, bottom: 4, containLabel: true },
    xAxis: {
      type: "value" as const,
      axisLabel: { color: "#9ca3af", fontSize: 10 },
      splitLine: { lineStyle: { color: "#f3f4f6" } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: "category" as const,
      data: sorted.map((c) => c.employer),
      inverse: true,
      axisLabel: { color: "#374151", fontSize: 11, width: 130, overflow: "truncate" as const },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        type: "bar" as const,
        data: sorted.map((c) => ({
          value: c.vacancies,
          itemStyle: { color, borderRadius: [0, 3, 3, 0], opacity: 0.85 },
        })),
        barMaxWidth: 16,
        label: { show: true, position: "right" as const, formatter: "{c}", color: "#9ca3af", fontSize: 10 },
      },
    ],
  };

  if (sorted.length === 0) {
    return <p className="text-xs text-gray-400 pt-2">Нет данных о компаниях</p>;
  }

  const h = Math.max(100, sorted.length * 26 + 12);
  return <ReactECharts option={option} style={{ height: h, width: "100%" }} notMerge />;
}
